import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface InviteUserParams {
  workspace_id: string;
  email: string;
  role_code: string;
  custom_message?: string;
}

export const useInviteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: InviteUserParams) => {
      console.log('[useInviteUser] Sending invitation:', params);
      
      // Check if user has active session and validate token
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session || !session.access_token) {
        console.error('[useInviteUser] Session validation failed:', sessionError);
        throw new Error('NO_AUTH: Authentication required. Please log in again.');
      }

      // Get user information for logging
      const { data: { user } } = await supabase.auth.getUser();
      console.log('[useInviteUser] Session valid, user:', user?.id, 'making API call');
      
      const { data, error } = await supabase.functions.invoke('workspace-invitations', {
        body: { action: 'create', ...params }
      });

      if (error) {
        console.error('[useInviteUser] Function error:', error);
        throw error;
      }

      if (!data?.ok) {
        console.error('[useInviteUser] Function returned error:', data);
        throw new Error(data?.message || 'Invitation failed');
      }

      console.log('[useInviteUser] Success:', data);
      return data;
    },
    onSuccess: (data, variables) => {
      // Invalidate related queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['access-control'] });
      queryClient.invalidateQueries({ queryKey: ['workspace-members'] });
      queryClient.invalidateQueries({ queryKey: ['workspace-invitations'] });
      
      toast.success('Invitation sent successfully!', {
        description: `Email invitation sent to ${variables.email}`
      });
    },
    onError: (error: any) => {
      console.error('[useInviteUser] Mutation error:', error);
      
      let errorMessage = 'Failed to send invitation';
      let errorDescription = error?.message || 'An unexpected error occurred';
      
      // Handle specific error codes from edge function
      if (error?.message?.includes('NO_AUTH') || error?.message?.includes('Authentication required')) {
        errorMessage = 'Authentication required';
        errorDescription = 'Please log in again to send invitations';
      } else if (error?.message?.includes('INSUFFICIENT_PERMISSIONS')) {
        errorMessage = 'Access denied';
        errorDescription = 'You do not have permission to invite users to this workspace';
      } else if (error?.message?.includes('MISSING_PARAMS') || error?.message?.includes('BAD_INPUT')) {
        errorMessage = 'Invalid input';
        errorDescription = 'Please check the email address and role, then try again';
      } else if (error?.message?.includes('CONFIG_ERROR')) {
        errorMessage = 'Configuration error';
        errorDescription = 'Service configuration issue - please contact support';
      } else if (error?.message?.includes('non-2xx status code')) {
        // Try to extract error from function response
        if (error?.context?.body) {
          try {
            const errorBody = JSON.parse(error.context.body);
            if (errorBody.message) {
              errorDescription = errorBody.message;
            } else if (errorBody.error) {
              errorDescription = errorBody.error;
            }
          } catch (e) {
            console.error('Failed to parse error body:', e);
          }
        }
      }
      
      toast.error(errorMessage, {
        description: errorDescription
      });
    }
  });
};